import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Clock } from 'lucide-react';
import { api, imgUrl, imgSrcSet } from '../lib/api';

const LatestPosts = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get('/blog').then((r) => setPosts(r.data.slice(0, 4))).catch(() => {});
  }, []);

  if (posts.length === 0) return null;

  return (
    <section id="guides" data-testid="latest-posts-section" className="relative mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.35em] text-cyber"
      >
        <span className="inline-block h-px w-10 bg-cyber/60" /> Journal de culture
      </motion.p>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="font-display text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl"
        >
          Derniers <span className="text-cyber">protocoles publiés.</span>
        </motion.h2>
        <Link
          to="/blog"
          data-testid="latest-posts-see-all"
          className="clip-frame-sm flex items-center gap-2 border border-cyber/40 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-cyber transition-all hover:bg-cyber hover:text-void"
        >
          Tous les guides <ArrowUpRight size={13} />
        </Link>
      </div>

      <div className={`mt-10 grid gap-6 sm:grid-cols-2 ${posts.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`} data-testid="latest-posts-grid">
        {posts.map((p, i) => (
          <motion.article
            key={p.id}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.6, delay: (i % 4) * 0.08 }}
            className="clip-frame group border border-white/8 bg-panel transition-colors hover:border-cyber/40"
            data-testid={`latest-post-${p.slug}`}
          >
            <Link to={`/blog/${p.slug}`} className="block">
              <div className="relative overflow-hidden">
                <img
                  srcSet={imgSrcSet(p.cover)}
                  sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
                  src={imgUrl(p.cover, 600)}
                  alt={p.title}
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-panel via-transparent to-transparent opacity-80" />
              </div>
              <div className="p-5">
                <p className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-cyber/80">
                  <Clock size={11} />
                  {(p.published_at || p.created_at || '').slice(0, 10).split('-').reverse().join('/')}
                </p>
                <h3 className="mt-3 font-display text-base font-bold leading-tight text-slate-100 transition-colors group-hover:text-cyber">
                  {p.title}
                </h3>
                <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-slate-400">{p.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 transition-colors group-hover:text-cyber">
                  Lire le protocole <ArrowUpRight size={12} />
                </span>
              </div>
            </Link>
          </motion.article>
        ))}
      </div>
    </section>
  );
};

export default LatestPosts;
